import { DataTableBaseColumn } from '../../common/table/TableTypes';
import { CardTypes, ConversationStatus, AiAssistantConversation } from './types';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import { DateKeys } from '../../common/datepicker/types';
import Box from '@mui/material/Box';
import { CheckCircle, UnreadComment, Warning } from '@sweep-io/sweep-design/dist/icons';

export const SUPPORT_AGENT_URL_PATH = 'support-agent';

export const columns: DataTableBaseColumn[] = [
  {
    field: 'user',
    headerName: 'User',
    minWidth: '160px',
  },
  {
    field: 'date',
    headerName: 'Date & time',
    minWidth: '160px',
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: '180px',
  },
  {
    field: 'page',
    headerName: 'Salesforce page',
  },
];

export const DEFAULT_DATEPICKER_STATE = DateKeys.Last28Days;

export const getConvertedStatus = (status: string) => {
  return ConversationStatus[status as keyof typeof ConversationStatus];
};

const getItemsInDateRange = (items: AiAssistantConversation[], startDate: Date, endDate: Date) => {
  return items.filter((el) => {
    const itemDate = new Date(el.updatedAt);
    return itemDate >= startDate && itemDate <= endDate;
  });
};

export const calculateDaysBetween = (startDate?: Date, endDate?: Date): number => {
  const start = startDate || new Date(0); // Use epoch time as default start
  const end = endDate || new Date(); // Use current time as default end
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const getCountByType = (
  type: CardTypes,
  tableData: AiAssistantConversation[],
  startDate: string,
  endDate: string,
) => {
  const startDateObj = new Date(startDate);
  const endDateObj = new Date(endDate);
  const items = getItemsInDateRange(tableData, startDateObj, endDateObj);
  switch (type) {
    case CardTypes.TotalSupportRequests:
      return items.length;

    case CardTypes.ResolutionRate:
      return items.length
        ? (
            (items.filter(
              (el) =>
                getConvertedStatus(el.status) === ConversationStatus.Closed_By_Admin ||
                getConvertedStatus(el.status) === ConversationStatus.Closed_By_Rep,
            ).length /
              items.length) *
            100
          ).toFixed(0)
        : 0;

    case CardTypes.AdminSupportRequests:
      return items.length
        ? (
            (items.filter((el) => getConvertedStatus(el.status) === ConversationStatus.Ask_Admin)
              .length /
              items.length) *
            100
          ).toFixed(0)
        : 0;

    case CardTypes.InProgress:
      return items.length
        ? (
            (items.filter((el) => getConvertedStatus(el.status) === ConversationStatus.Open)
              .length /
              items.length) *
            100
          ).toFixed(0)
        : 0;
  }
};

export const getPercentageByType = (
  type: CardTypes,
  tableData: AiAssistantConversation[],
  startDate: string,
  endDate: string,
) => {
  const startDateObj = new Date(startDate);
  const endDateObj = new Date(endDate);
  const lastXDaysItems = getItemsInDateRange(tableData, startDateObj, endDateObj);

  const daysBetween = calculateDaysBetween(startDateObj, endDateObj);
  const prevEndDateObj = new Date(endDateObj.getTime() - daysBetween * 24 * 60 * 60 * 1000);
  const prevStartDateObj = new Date(startDateObj.getTime() - daysBetween * 24 * 60 * 60 * 1000);
  const previousXDaysItems = getItemsInDateRange(tableData, prevStartDateObj, prevEndDateObj);

  const getFilteredCount = (items: AiAssistantConversation[]) => {
    switch (type) {
      case CardTypes.TotalSupportRequests:
        return items.length;

      case CardTypes.ResolutionRate:
        return items.filter(
          (el) =>
            getConvertedStatus(el.status) === ConversationStatus.Closed_By_Admin ||
            getConvertedStatus(el.status) === ConversationStatus.Closed_By_Rep,
        ).length;

      case CardTypes.AdminSupportRequests:
        return items.filter((el) => getConvertedStatus(el.status) === ConversationStatus.Ask_Admin)
          .length;

      case CardTypes.InProgress:
        return items.filter((el) => getConvertedStatus(el.status) === ConversationStatus.Open)
          .length;
    }
  };

  const lastXDaysCount = getFilteredCount(lastXDaysItems);
  const previousXDaysCount = getFilteredCount(previousXDaysItems);

  if (previousXDaysCount === 0) {
    return Math.round((lastXDaysCount / 1) * 100);
  }

  return Math.round(((lastXDaysCount - previousXDaysCount) / previousXDaysCount) * 100);
};

export const getPageLink = (data?: AiAssistantConversation, isCollapsed?: boolean) => {
  if (data?.pageUrl === 'N/A' || !data?.pageUrl) {
    return (
      <Box sx={{ '& span': { color: `${colors.grey[500]} !important` } }}>
        <Typography variant={isCollapsed ? 'caption' : 'body'} color={colors.grey[500]}>
          Page is unavailable
        </Typography>
      </Box>
    );
  } else {
    return (
      <Button
        onClick={(e) => {
          e.stopPropagation();
          window.open(data.pageUrl, '_blank');
        }}
        variant="flat-blue"
        size={isCollapsed ? 'tiny' : 'small'}
        endIconName="ViewInSalesforce"
      >
        Show page
      </Button>
    );
  }
};

export const getStatusIconByType = (type: ConversationStatus) => {
  if (getConvertedStatus(type) === ConversationStatus.Ask_Admin) {
    return <Warning size="medium" color={colors.blush[600]} />;
  }
  if (getConvertedStatus(type) === ConversationStatus.Open) {
    return <UnreadComment size="medium" color={colors.blue[600]} />;
  } else {
    return <CheckCircle size="medium" color={colors.mint[700]} />;
  }
};
